import React, { useCallback } from "react"
import { BaseVisualizationConfigureModal } from "./BaseVisualizationConfigureModal"
import { ElementConfigureDiv } from "../../../../../../Constants/StyledComponents"
import { WAVEFORM_WINDOW_TIME_PRESETS } from "../../../../../../Managers/VisualizationManager/Viewport/Components/XAxis"
import { EEGMontageConfigJSON } from "../../Types/EEGMontageConfig"
import { eegMontageConfigAtom } from "../../Atoms/EEGMontage"
import { EEG_LOW_FILTERS, EEG_HIGH_FILTERS, EEG_NOTCH_FILTERS, EEG_SENSITIVITY } from "../../Types/EEG"
import { useEditWindow } from "./useEditWindow"
import { MobergDropdown, MobergDropdownSize } from "../../../../../../Components/MobergDropdown/MobergDropdown"
import { DataReviewEEGMontageDropdown } from "../../../../../../Components/EEGMontageDropdownMenu/DataReviewEEGMontageDropdown"
import { SetterOrUpdater } from "recoil"

type ConfigureEEGModalProps = {
	layoutId: string
	windowId: string
	escClose?: boolean
	clickOutsideClose?: boolean
}

export const ConfigureEEGModal = (props: ConfigureEEGModalProps) => {
	const atom = eegMontageConfigAtom({ windowId: props.windowId, layoutId: props.layoutId })
	const { editedConfig, saveChanges, updateProperty, updateProps } = useEditWindow<EEGMontageConfigJSON>({ recoilState: atom, windowId: props.windowId, layoutId: props.layoutId })

	// This setter updates the props on the layout and works just like a react setState hook.
	const setEEGConfig: SetterOrUpdater<EEGMontageConfigJSON> = useCallback(newValueOrSetter => {
		if (newValueOrSetter instanceof Function) {
			updateProps(newValueOrSetter(editedConfig))
		} else {
			updateProps(newValueOrSetter)
		}
	}, [editedConfig, updateProps])

	return (
		<BaseVisualizationConfigureModal
			layoutId={props.layoutId}
			windowId={props.windowId}
			escClose={props.escClose}
			clickOutsideClose={props.clickOutsideClose}
			saveChanges={saveChanges}
			title={"Configure EEG Montage"}
		>
			<div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
				<div style={{ padding: "20px", width: "400px" }}>

					<ElementConfigureDiv>
						Montage

						<DataReviewEEGMontageDropdown 
							size={MobergDropdownSize.REGULAR}
							placement={"right-start"}
							currentMontage={editedConfig.montage}
							updateCurrentMontage={montage => setEEGConfig(previous => ({...previous, montage }))}
						/>
					</ElementConfigureDiv>

					<ElementConfigureDiv>
						Initial Page Size

						<MobergDropdown
							selectedValue={editedConfig?.viewDuration}
							onChange={value => updateProperty("viewDuration", parseInt(value as string))}
							options={WAVEFORM_WINDOW_TIME_PRESETS
								.filter(preset => preset.label !== "Page Size")
								.map(preset => ({ label: preset.label, value: preset.time }))}
						/>
					</ElementConfigureDiv>

					<ElementConfigureDiv>
						LFF

						<MobergDropdown
							selectedValue={editedConfig?.LFF}
							onChange={value => updateProperty("LFF", parseFloat(value as string))}
							options={Object.entries(EEG_LOW_FILTERS).map(([label, value]) => ({ label, value }))}
						/>
					</ElementConfigureDiv>

					<ElementConfigureDiv>
						HFF

						<MobergDropdown
							selectedValue={editedConfig?.HFF}
							onChange={value => updateProperty("HFF", parseFloat(value as string))}
							options={Object.entries(EEG_HIGH_FILTERS).map(([label, value]) => ({ label, value }))}
						/>
					</ElementConfigureDiv>

					<ElementConfigureDiv>
						Notch Filter Frequency

						<MobergDropdown
							selectedValue={editedConfig?.notch}
							onChange={value => updateProperty("notch", parseFloat(value as string))}
							options={Object.entries(EEG_NOTCH_FILTERS).map(([label, value]) => ({ label, value }))}
						/>
					</ElementConfigureDiv>

					<ElementConfigureDiv>
						Sensitivity
						<MobergDropdown
							selectedValue={editedConfig?.sensitivityMicroVolts}
							onChange={value => updateProperty("sensitivityMicroVolts", parseFloat(value as string))}
							options={Object.entries(EEG_SENSITIVITY).map(([label, value]) => ({ label, value }))}
						/>
					</ElementConfigureDiv>
				</div>
			</div>
		</BaseVisualizationConfigureModal>
	)
}
